import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"
import { Toaster } from "@/components/ui/sonner"
import { Suspense, lazy } from "react"

import { AuthProvider, useAuth } from "./contexts/auth-context"
import { ThemeProvider } from "./contexts/theme-context"
import { AuthLayout } from "./layouts/AuthLayout"
import { DashboardLayout } from "./layouts/DashboardLayout"
import { QueryProvider } from "./providers/query-provider"
import { SocketProvider } from "./providers/socket-provider"

// Lazy load all page components
const LoginPage = lazy(() => import("./features/auth/LoginPage").then(module => ({ default: module.LoginPage })))
const SignupPage = lazy(() => import("./features/auth/SignupPage").then(module => ({ default: module.SignupPage })))
const ForgotPasswordPage = lazy(() => import("./features/auth/ForgotPasswordPage").then(module => ({ default: module.ForgotPasswordPage })))
const ResetPasswordPage = lazy(() => import("./features/auth/ResetPasswordPage").then(module => ({ default: module.ResetPasswordPage })))
const TermsPage = lazy(() => import("./features/legal/TermsPage").then(module => ({ default: module.TermsPage })))
const PrivacyPage = lazy(() => import("./features/legal/PrivacyPage").then(module => ({ default: module.PrivacyPage })))
const DashboardPage = lazy(() => import("./features/dashboard/DashboardPage").then(module => ({ default: module.DashboardPage })))
const UsersPage = lazy(() => import("./features/users/UsersPage").then(module => ({ default: module.UsersPage })))
const UserPage = lazy(() => import("./features/users/UserPage").then(module => ({ default: module.UserPage })))
const UserProfilePage = lazy(() => import("./features/users/UserProfilePage").then(module => ({ default: module.UserProfilePage })))
const MatchesPage = lazy(() => import("./features/matches/MatchesPage").then(module => ({ default: module.MatchesPage })))
const MatchPage = lazy(() => import("./features/matches/MatchPage").then(module => ({ default: module.MatchPage })))
const ScoresPage = lazy(() => import("./features/matches/ScoresPage").then(module => ({ default: module.ScoresPage })))
const SettingsPage = lazy(() => import("./features/settings/SettingsPage").then(module => ({ default: module.SettingsPage })))
const OrganizationsPage = lazy(() => import("./features/organizations/OrganizationsPage").then(module => ({ default: module.OrganizationsPage })))
const OrganizationPage = lazy(() => import("./features/organizations/OrganizationPage").then(module => ({ default: module.OrganizationPage })))
const SubscriptionSuccessPage = lazy(() => import("./features/subscription/SubscriptionSuccessPage").then(module => ({ default: module.SubscriptionSuccessPage })))
const SubscriptionCancelPage = lazy(() => import("./features/subscription/SubscriptionCancelPage").then(module => ({ default: module.SubscriptionCancelPage })))

// Loading fallback component
function LoadingFallback() {
  return <div className="flex items-center justify-center h-screen">Loading...</div>
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingFallback />; // Use the same loading component for consistency
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function App() {
  return (
    <QueryProvider>
      <AuthProvider>
        <BrowserRouter>
          <ThemeProvider>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Legal pages - full width */}
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />

                {/* Subscription pages - full width */}
                <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
                <Route path="/subscription/cancel" element={<SubscriptionCancelPage />} />

                {/* Public routes */}
                <Route element={<AuthLayout />}>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                </Route>

                {/* Protected routes */}
                <Route
                  element={
                    <ProtectedRoute>
                      <SocketProvider>
                        <DashboardLayout />
                      </SocketProvider>
                    </ProtectedRoute>
                  }
                >
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/profile" element={<UserProfilePage />} />
                  <Route path="/users" element={<UsersPage />} />
                  <Route path="/users/:id" element={<UserPage />} />
                  <Route path="/organizations" element={<OrganizationsPage />} />
                  <Route path="/organizations/:id" element={<OrganizationPage />} />
                  <Route path="/matches" element={<MatchesPage />} />
                  <Route path="/matches/:id" element={<MatchPage />} />
                  <Route path="/matches/:id/scores" element={<ScoresPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                </Route>

                {/* Redirect root to dashboard or login */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Navigate to="/dashboard" replace />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Suspense>
            <Toaster />
          </ThemeProvider>
        </BrowserRouter>
      </AuthProvider>
    </QueryProvider>
  )
}

export default App
